import React, { useRef, useState } from 'react';
import APP_PATHS from '../../constants/app-paths';
import { Button } from '@llt-frontend/react-material-button';
import CAPABILITIES from '../../constants/capabilities';
import { Link } from 'react-router-dom';
import { Menu } from '@llt-frontend/react-material-menu';
import StudentBadge from '../students/student-badge';
import { Tooltip } from '@llt-frontend/react-material-tooltip';
import { useInteractionTracker } from '@blueshift-ui/telemetry';
import { useLDClient as useLaunchDarklyClient } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import useUserContext from '../../hooks/context/use-user-context';

function _ActiveProfile({ student, colorIndex }) {
  return (
    <>
      <StudentBadge
        colorIndex={colorIndex}
        firstName={student.firstName}
        lastName={student.lastName}
        small
      />

      <span className="user-menu-trigger__title">{student.firstName}</span>
    </>
  );
}

//

function UserMenu() {
  const launchDarklyClient = useLaunchDarklyClient();
  const { activeStudent, students = [], hasCapability } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleOnMouseEnter = () => {
    clearTimeout(closeTimeoutRef.current);
    setIsOpen(true);
  };

  const handleOnMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  React.useEffect(() => {
    return () => clearTimeout(closeTimeoutRef.current);
  }, []);

  React.useEffect(() => {
    launchDarklyClient?.identify({ key: activeStudent.uuid });
  }, [launchDarklyClient, activeStudent]);

  const { t: translate, ready: translationLoaded } = useTranslation('user-menu/index');
  const { trackEvent } = useInteractionTracker();

  //
  // Methods

  const handleMenuItemClick = React.useCallback(
    (_, { value }) => {
      trackEvent({
        action: `click-${value}`,
        category: 'profile-nav',
      });
    },
    [trackEvent]
  );

  function getItems() {
    let items = [];

    if (students.length > 1) {
      items.push({
        component: Link,
        graphic: 'swap_horiz',
        primaryText: translate('switchStudent'),
        to: APP_PATHS.selectProfile,
        value: 'switch-student',
      });
    }

    items.push({
      component: Link,
      graphic: 'add',
      primaryText: translate('addStudent'),
      to: APP_PATHS.createProfile,
      value: 'add-student',
    });

    return items;
  }

  //
  // Render

  if (!translationLoaded || !activeStudent) {
    return null;
  }

  if (!hasCapability(CAPABILITIES.profileManagement)) {
    return (
      <Tooltip
        className="user-menu__tooltip"
        id="student-info"
        label={
          <>
            {activeStudent.firstName} {activeStudent.lastName}
            {activeStudent.email && (
              <>
                <br />({activeStudent.email})
              </>
            )}
          </>
        }
      >
        <div className="user-menu active-profile">
          <_ActiveProfile colorIndex={students.indexOf(activeStudent)} student={activeStudent} />
        </div>
      </Tooltip>
    );
  }

  return (
    <div id="user-menu" onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
      <Menu
        className="user-menu"
        items={getItems()}
        key={isOpen}
        onItemClick={handleMenuItemClick}
        onMouseLeave={handleOnMouseLeave}
        open={isOpen}
        openHandlerName="onMouseEnter"
      >
        <Button className="user-menu-trigger" iconName="arrow_drop_down" trailingIcon>
          <_ActiveProfile colorIndex={students.indexOf(activeStudent)} student={activeStudent} />
        </Button>
      </Menu>
    </div>
  );
}

// Exports
// -------

export default UserMenu;
